import { Lucide } from '../../../index';
import {
  Box,
  Button,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Clock, MapPin } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDraggable } from 'react-use-draggable-scroll';
import { AppointmentProjectSelector } from './appointment-project-selector';
import { AppointmentModalHeader } from './shared';
import { EAppointmentType, TAppointmentProjectMinimal } from './types';
import { TimeOption } from './utils';
import { useAppointmentModalActions, useAppointmentModalState } from './store';

interface AppointmentStep1Props {
  projects: TAppointmentProjectMinimal[];
  onClose: (stepNr: number) => void;
  onContinue?: () => void;
  t: (key: string) => string;
  warningBanner?: React.ReactNode;
  hideDuration?: boolean;
  logoSrc?: string;
  isLanding?: boolean;
}

export const AppointmentStep1: React.FC<AppointmentStep1Props> = ({
  projects,
  onClose,
  onContinue,
  t,
  warningBanner,
  hideDuration = false,
  logoSrc,
  isLanding = false,
}) => {
  const {
    getTimeSlotsForDate,
    setProjectId,
    setDateTimeSlot,
    setStep,
    setAppointmentType,
    setDuration,
    setNotes,
    setSelectedDate,
    setSelectedTime,
    setTimeOptions,
  } = useAppointmentModalActions();
  const {
    projectId,
    appointmentType,
    duration,
    notes,
    dateOptions,
    selectedDate,
    selectedTime,
    timeOptions,
  } = useAppointmentModalState();

  const dateScrollRef = useRef<HTMLDivElement>(null!);
  const timeScrollRef = useRef<HTMLDivElement>(null!);
  const isDateScrollReady = dateOptions.length > 0;
  const isTimeScrollReady = timeOptions.length > 0;

  const { events: dateScrollEvents } = useDraggable(dateScrollRef, {
    isMounted: isDateScrollReady,
  });
  const { events: timeScrollEvents } = useDraggable(timeScrollRef, {
    isMounted: isTimeScrollReady,
  });

  const [showProjectError, setShowProjectError] = useState<boolean>(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState<boolean>(true);

  // auto-scroll on initial load only
  useEffect(() => {
    if (!shouldAutoScroll || !isDateScrollReady || !selectedDate || dateOptions.length === 0) {
      return;
    }

    const selectedIndex = dateOptions.findIndex(
      (option) => option.date.getTime() === selectedDate.getTime(),
    );

    if (selectedIndex === -1) return;

    // timeout to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      const scrollContainer = dateScrollRef.current;
      if (!scrollContainer) return;

      const buttons = scrollContainer.querySelectorAll('button');
      const targetButton = buttons[selectedIndex];

      if (targetButton) {
        // center the selected button
        targetButton.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }

      setShouldAutoScroll(false); // disable auto-scroll after first use
    }, 150);

    return () => clearTimeout(timeoutId);
  }, [isDateScrollReady, selectedDate, dateOptions, shouldAutoScroll]);

  // Translated duration options (values in minutes)
  const durationOptions = useMemo(
    () => [
      { value: 30, label: t('scheduling.appointment.duration30Min') },
      { value: 60, label: t('scheduling.appointment.duration1Hour') },
      { value: 90, label: t('scheduling.appointment.duration1_5Hours') },
      { value: 120, label: t('scheduling.appointment.duration2Hours') },
    ],
    [t],
  );
  // Translated appointment type options
  const appointmentTypeOptions = useMemo(
    () => [
      {
        value: EAppointmentType.GOOGLE_MEET,
        label: t('scheduling.appointment.googleMeet'),
        icon: (
          <Box
            component="img"
            src="https://static.treedis.com/scheduling/email-templates/assets/google_meet_icon.png"
            alt="Google Meet"
            sx={{ width: 20, height: 20 }}
          />
        ),
      },
      {
        value: EAppointmentType.PHYSICAL,
        label: t('scheduling.appointment.physicalMeeting'),
        icon: <MapPin size={16} />,
      },
    ],
    [t],
  );

  const handleProjectChange = (projectId?: number) => {
    // selected or first project
    if (projectId) {
      setProjectId(projectId);
      setShowProjectError(false);
    } else if (projects.length > 0 && !projectId) {
      setProjectId(projects[0].id);
      setShowProjectError(false);
    }
  };

  // Handle date selection
  const handleDateSelect = useCallback(
    (index: number) => {
      const newDate = dateOptions[index].date;
      setShouldAutoScroll(false); // disable auto-scroll for manual selections
      setSelectedDate(newDate);

      const timeOptions = getTimeSlotsForDate(newDate);
      setTimeOptions(timeOptions);

      if (timeOptions.length > 0) {
        setSelectedTime(timeOptions[0]);
      } else {
        setSelectedTime(undefined);
      }
    },
    [dateOptions, getTimeSlotsForDate],
  );

  // Handle time selection
  const handleTimeSelect = useCallback((timeOption: TimeOption) => {
    setSelectedTime(timeOption);
  }, []);

  const handleContinue = useCallback(() => {
    if (!projectId) {
      setShowProjectError(true);
      return;
    }

    if (selectedDate && selectedTime) {
      setDateTimeSlot({
        start: selectedTime.start,
        end: selectedTime.end,
      });
      onContinue?.();
      setStep(2);
    }
  }, [selectedDate, selectedTime]);

  return (
    <Stack spacing={3} width="100%">
      <AppointmentModalHeader
        onClose={() => onClose(1)}
        t={t}
        logoSrc={logoSrc}
        isLanding={isLanding}
        step={1}
      />

      {warningBanner}

      {projects.length > 1 && (
        <AppointmentProjectSelector
          selectedProjectId={projectId}
          onProjectChange={handleProjectChange}
          projects={projects}
          error={showProjectError}
          helperText={showProjectError ? t('scheduling.appointment.projectRequired') : ''}
          t={t}
        />
      )}

      <Stack spacing={1} width="100%">
        <Typography variant="body2" fontWeight={500} color="text.secondary">
          {t('scheduling.appointment.selectDate')}
        </Typography>
        <Box
          ref={dateScrollRef}
          {...(isDateScrollReady ? dateScrollEvents : {})}
          sx={{
            overflow: 'auto',
            width: '100%',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          <Stack direction="row" gap={1.25} py={0.25} px={0.5}>
            {dateOptions.length === 0 ? (
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                width="100%"
                py={0.25}
                sx={{
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'grey.300',
                  height: 82,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('scheduling.appointment.noAvailableDates')}
                </Typography>
              </Stack>
            ) : (
              dateOptions.map((dateOption, index) => {
                const isSelected =
                  selectedDate && selectedDate.getTime() === dateOption.date.getTime();
                return (
                  <Button
                    key={`${dateOption.day}-${dateOption.dateStr}-${dateOption.date.getTime()}`}
                    onClick={() => handleDateSelect(index)}
                    variant="outlined"
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 82,
                      height: 82,
                      p: 1.75,
                      borderRadius: '12px',
                      border: isSelected ? 2 : 1,
                      borderColor: isSelected ? 'primary.main' : 'grey.300',
                      flexShrink: 0,
                      minWidth: 'unset',
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        borderColor: isSelected ? 'primary.main' : 'grey.400',
                      },
                    }}
                  >
                    <Typography color="text.secondary" variant="body1">
                      {dateOption.day}
                    </Typography>
                    <Typography color="text.primary" variant="body1" fontWeight={600}>
                      {dateOption.dateStr}
                    </Typography>
                  </Button>
                );
              })
            )}
          </Stack>
        </Box>
      </Stack>

      <Stack spacing={1} width="100%">
        <Typography variant="body2" fontWeight={500} color="text.secondary">
          {t('scheduling.appointment.selectTime')}
        </Typography>
        {timeOptions.length === 0 ? (
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            width="100%"
            py={0.25}
            sx={{
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: 1,
              borderColor: 'grey.300',
              height: 42,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('scheduling.appointment.noAvailableTimeSlots')}
            </Typography>
          </Stack>
        ) : (
          <Box
            ref={timeScrollRef}
            {...(isTimeScrollReady ? timeScrollEvents : {})}
            sx={{
              overflow: 'auto',
              width: '100%',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            <Stack direction="row" gap={1.25} py={0.25} px={0.5}>
              {timeOptions.map((timeOption, index) => {
                const isSelected = selectedTime && selectedTime.start === timeOption.start;
                return (
                  <Button
                    key={`${timeOption.time}-${index}`}
                    onClick={() => handleTimeSelect(timeOption)}
                    variant="outlined"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: 'none',
                      px: 1.75,
                      py: 1.25,
                      borderRadius: '12px',
                      border: isSelected ? 2 : 1,
                      borderColor: isSelected ? 'primary.main' : 'grey.300',
                      flexShrink: 0,
                      minWidth: 'unset',
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        borderColor: isSelected ? 'primary.main' : 'grey.400',
                      },
                    }}
                  >
                    <Typography color="text.primary" variant="body1" fontWeight={600}>
                      {timeOption.time}
                    </Typography>
                  </Button>
                );
              })}
            </Stack>
          </Box>
        )}
      </Stack>

      <Stack direction="row" spacing={2.5} width="100%">
        {!hideDuration && (
          <Stack spacing={0.75} width="100%">
            <Typography variant="body2" fontWeight={500} color="text.secondary">
              {t('scheduling.appointment.duration')}
            </Typography>
            <Select
              size="small"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              startAdornment={
                <InputAdornment position="start">
                  <Lucide icon={Clock} />
                </InputAdornment>
              }
              MenuProps={{
                sx: {
                  zIndex: 10000, // Ensure dropdown appears above mobile drawer
                },
              }}
            >
              {durationOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        )}

        <Stack spacing={0.75} width="100%">
          <Typography variant="body2" fontWeight={500} color="text.secondary">
            {t('scheduling.appointment.appointmentType')}
          </Typography>
          <Select
            size="small"
            value={appointmentType}
            onChange={(e) => setAppointmentType(e.target.value as EAppointmentType)}
            MenuProps={{
              sx: {
                zIndex: 10000, // Ensure dropdown appears above mobile drawer
              },
            }}
          >
            {appointmentTypeOptions.map((option, index) => (
              <MenuItem key={index} value={option.value}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      background: 'white',
                      borderRadius: 0.5,
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    {option.icon}
                  </Box>
                  {option.label}
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </Stack>

      <Stack spacing={0.75} width="100%">
        <Typography variant="body2" fontWeight={500} color="text.secondary">
          {t('scheduling.appointment.notes')}
        </Typography>
        <TextField
          multiline
          rows={5}
          placeholder={t('scheduling.appointment.notesPlaceholder')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          sx={{ borderRadius: 2 }}
          slotProps={{
            htmlInput: { maxLength: 300 },
          }}
        />
      </Stack>

      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleContinue}
        disabled={!selectedDate || !selectedTime}
      >
        {t(`scheduling.appointment.${isLanding ? 'continueLanding' : 'continue'}`)}
      </Button>
    </Stack>
  );
};
